import { countryOptions } from "./countryOptions";
import { t } from "@/locales";
import { BookingLanguage } from "@/types/booking";
import {
  MarketCountry,
  DEFAULT_MARKET,
  getLegalUrls,
  getMembershipTermsUrl,
} from "@/config/market";
import { TranslationType } from "./types";

export { countryOptions };

// Helper to build TranslationType from flat keys
const buildBookingFormTranslations = (
  lang: BookingLanguage,
  market: MarketCountry = DEFAULT_MARKET,
): TranslationType => {
  const legal = getLegalUrls(market, lang);
  return {
    firstName: t(lang, "bookingForm.firstName"),
    lastName: t(lang, "bookingForm.lastName"),
    firstNameRequired: t(lang, "bookingForm.firstNameRequired"),
    lastNameRequired: t(lang, "bookingForm.lastNameRequired"),
    email: t(lang, "bookingForm.email"),
    emailRequired: t(lang, "bookingForm.emailRequired"),
    emailInvalid: t(lang, "bookingForm.emailInvalid"),
    birthday: t(lang, "bookingForm.birthday"),
    birthdayDay: t(lang, "bookingForm.birthdayDay"),
    birthdayMonth: t(lang, "bookingForm.birthdayMonth"),
    birthdayYear: t(lang, "bookingForm.birthdayYear"),
    phone: t(lang, "bookingForm.phone"),
    phoneNumber: t(lang, "bookingForm.phoneNumber"),
    phoneInstructions: t(lang, "bookingForm.phoneInstructions"),
    phoneInvalid: t(lang, "bookingForm.phoneInvalid"),
    phoneRequired: t(lang, "bookingForm.phoneRequired"),
    genderAtBirth: t(lang, "bookingForm.genderAtBirth"),
    male: t(lang, "bookingForm.male"),
    female: t(lang, "bookingForm.female"),
    other: t(lang, "bookingForm.other"),
    preferNotToSay: t(lang, "bookingForm.preferNotToSay"),
    additionalNotes: t(lang, "bookingForm.additionalNotes"),
    notes: t(lang, "bookingForm.notes"),
    gdprConsent: t(lang, "bookingForm.gdprConsent"),
    gdprLink: t(lang, "bookingForm.gdprLink"),
    gdprRequired: t(lang, "bookingForm.gdprRequired"),
    submitDirect: t(lang, "bookingForm.submitDirect"),
    submitRequest: t(lang, "bookingForm.submitRequest"),
    birthdayRequired: t(lang, "bookingForm.birthdayRequired"),
    birthdayInvalid: t(lang, "bookingForm.birthdayInvalid"),
    birthdayFuture: t(lang, "bookingForm.birthdayFuture"),
    birthdayMinAge: t(lang, "bookingForm.birthdayMinAge"),
    birthdayMaxAge: t(lang, "bookingForm.birthdayMaxAge"),
    emailPlaceholder: t(lang, "bookingForm.emailPlaceholder"),
    supportRequirements: t(lang, "bookingForm.supportRequirements"),
    anxiousPatient: t(lang, "bookingForm.anxiousPatient"),
    anxiousPatientSubtext: t(lang, "bookingForm.anxiousPatientSubtext"),
    preferredLanguage: t(lang, "bookingForm.preferredLanguage"),
    selectLanguage: t(lang, "bookingForm.selectLanguage"),
    selectCountry: t(lang, "bookingForm.selectCountry"),
    genderRequired: t(lang, "bookingForm.genderRequired"),
    street: t(lang, "bookingForm.street"),
    streetRequired: t(lang, "bookingForm.streetRequired"),
    streetNumber: t(lang, "bookingForm.streetNumber"),
    streetNumberRequired: t(lang, "bookingForm.streetNumberRequired"),
    zipCode: t(lang, "bookingForm.zipCode"),
    zipCodeRequired: t(lang, "bookingForm.zipCodeRequired"),
    city: t(lang, "bookingForm.city"),
    cityRequired: t(lang, "bookingForm.cityRequired"),
    country: t(lang, "bookingForm.country"),
    countryRequired: t(lang, "bookingForm.countryRequired"),
    countries: t(lang, "bookingForm.countries"),
    allCountries: t(lang, "bookingForm.allCountries"),
    otherCountry: t(lang, "bookingForm.otherCountry"),
    otherCountryPlaceholder: t(lang, "bookingForm.otherCountryPlaceholder"),
    formValidationError: t(lang, "bookingForm.formValidationError"),
    // Privacy consent fields (map from locale keys to component expected keys)
    privacyconsentfield_TosAndPp_Part1: t(
      lang,
      "privacyConsent.tosAndPp.part1",
    ),
    privacyconsentfield_TosOnly_Part1: t(lang, "privacyConsent.tosOnly.part1"),
    privacyconsentfield_TosOnly_TermsLink: t(
      lang,
      "privacyConsent.tosOnly.termsLink",
    ),
    privacyconsentfield_TosOnly_Part2: t(lang, "privacyConsent.tosOnly.part2"),
    privacyconsentfield_TosAndPp_TermsLink: t(
      lang,
      "privacyConsent.tosAndPp.termsLink",
    ),
    privacyconsentfield_TosAndPp_Part2: t(
      lang,
      "privacyConsent.tosAndPp.part2",
    ),
    privacyconsentfield_TosAndPp_PrivacyLink: t(
      lang,
      "privacyConsent.tosAndPp.privacyLink",
    ),
    privacyconsentfield_TosAndPp_Part3: t(
      lang,
      "privacyConsent.tosAndPp.part3",
    ),
    privacyconsentfield_MedicalData_Part1: t(
      lang,
      "privacyConsent.medicalData.part1",
    ),
    privacyconsentfield_MedicalData_ConsentLink: t(
      lang,
      "privacyConsent.medicalData.consentLink",
    ),
    privacyconsentfield_MedicalData_Part2: t(
      lang,
      "privacyConsent.medicalData.part2",
    ),
    privacyconsentfield_MedicalDataCh_Part1: t(
      lang,
      "privacyConsent.medicalDataCh.part1",
    ),
    privacyconsentfield_MedicalDataCh_ConsentLink: t(
      lang,
      "privacyConsent.medicalDataCh.consentLink",
    ),
    privacyconsentfield_MedicalDataCh_Part2: t(
      lang,
      "privacyConsent.medicalDataCh.part2",
    ),
    privacyconsentfield_Optional_Text: t(lang, "privacyConsent.optional.text"),
    privacyconsentfield_Optional_Tooltip: t(
      lang,
      "privacyConsent.optional.tooltip",
    ),
    privacyconsentfield_RequiredError: t(lang, "privacyConsent.requiredError"),
    privacy_policy_url: legal.pp,
    terms_conditions_url: legal.tos,
    membership_terms_url: getMembershipTermsUrl(lang),
    medical_data_sharing_url: legal.mds,
  };
};

// Build translations dynamically (called at render time, not module load time)
// This ensures i18next has loaded its translations before building the object
export const getTranslations = (
  lang: BookingLanguage,
  market: MarketCountry = DEFAULT_MARKET,
): TranslationType => {
  return buildBookingFormTranslations(lang, market);
};
